import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Slide, ToastContainer, toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { municipalitiesManagement } from "../../../../../../hooks/api/MunicipalitiesManagement/MunicipalitiesManagement";
import { DefaultLayout } from "../../../../../../layouts/Default";
import { TopNavigation } from "../../../../../../themes/Default/components/TopNavigation";
import { Button, ButtonBack } from "../../../../../../themes/Default/components/TopNavigation/components/Button";
import { Tabs } from "../../../../../../themes/Default/components/Tabs";
import { Main } from "../../../../../../themes/Default/components/Main";
import { Title } from "../../../../../../themes/Default/components/Title";
import { Menu as ListMenu } from "../../../../../../themes/Default/components/List/components/Header/components/Menu";
import { Filter as ListFilter } from "../../../../../../themes/Default/components/List/components/Header/components/Filter";
import { Button as ListMenuButton } from "../../../../../../themes/Default/components/List/components/Header/components/Menu/components/Button";
import { Breadcrumps } from "../../../../../../themes/Default/components/Breadcrumps";
import { Link } from "../../../../../../themes/Default/components/Breadcrumps/components/Link";
import { List } from "../../../../../../themes/Default/components/List";
import { Body as ListBody } from "../../../../../../themes/Default/components/List/components/Body";
import { Header as ListHeader } from "../../../../../../themes/Default/components/List/components/Header";
import { TableHead } from "../../../../../../themes/Default/components/List/components/Body/components/TableHead";
import { TableRow } from "../../../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow";
import { TableHeadCell } from "../../../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow/components/TableHeadCell";
import { TableBody } from "../../../../../../themes/Default/components/List/components/Body/components/TableBody";
import { TableDataCell } from "../../../../../../themes/Default/components/List/components/Body/components/TableBody/components/TableDataCell";
import { Checkbox } from "../../../../../../themes/Default/components/Form/components/Checkbox";
import { Form } from "../../../../../../themes/Default/components/Form";
import { Row } from "../../../../../../themes/Default/components/Form/components/Row";
import { Field } from "../../../../../../themes/Default/components/Form/components/Row/components/Field";
import { Input } from "../../../../../../themes/Default/components/Form/components/Input";
import { Formik } from "formik";
import { Pagination } from "../../../../../../themes/Default/components/List/components/Footer/components/Pagination";
import { getNumberOfUsedFilters } from "../../../../../../services/utils/Filter";
import { Toaster } from "../../../../../../themes/Default/components/Toaster";

const Municipalities = () => {
    const { t } = useTranslation("common");
    const navigate = useNavigate();
    const recordsPerPage = 10;
    const defaultFilters = {
        startPage: 0,
        limitPage: recordsPerPage,
    };
    const [filters, setFilters] = useState(defaultFilters);
    const [currentPage, setCurrentPage] = useState(1);
    const [showFilter, setShowFilter] = useState(false);
    const [numberOfUserdFilters, setNumberOfUserdFilters] = useState();
    const formRef = useRef(null);
    const [totalRecords, setTotalRecords] = useState(0);
    const [disableDelete, setDisableDelete] = useState(true);
    const [selectedMunicipality, setSelectedMunicipality] = useState([]);
    const [municipalities, setMunicipalities] = useState();
    const { useMunicipalitiesListMutation, useDeleteMunicipalitiesMutation } = municipalitiesManagement;
    const [municipalityList, { isLoading }] = useMunicipalitiesListMutation();
    const [deleteMunicipality] = useDeleteMunicipalitiesMutation();

    const tabs = [
        { title: t("noteTypes.title"), onClick: () => gotoNoteTypes() },
        { title: t("guardianTypes.title"), onClick: () => gotoGuardianType() },
        { title: t("municipalities.title"), isActive: true },
    ];

    const gotoGuardianType = () => {
        navigate("/settings/default/guardian-types");
    };

    const gotoNoteTypes = () => {
        navigate("/settings/default/note-types");
    };

    const gotoNew = () => {
        navigate("/settings/default/municipalities/add");
    };

    const gotoEdit = (municipalityId) => {
        navigate("/settings/default/municipalities/edit/" + municipalityId);
    };

    const onSelectionChange = (id) => {
        setSelectedMunicipality((prevSelected) => {
            if (prevSelected.includes(id)) {
                return prevSelected.filter((recordId) => recordId !== id);
            } else {
                return [...prevSelected, id];
            }
        });
    };

    const onDelete = () => {
        toast.warn(
            ({ closeToast }) => (
                <Toaster
                    closeToast={closeToast}
                    description={t("areYouSure")}
                    optionNo={t("no")}
                    optionYes={t("yes")}
                    onClick={() => {
                        closeToast();
                        for (let i = 0; i < selectedMunicipality?.length; i++) {
                            const municipalityId = selectedMunicipality[i];
                            deleteMunicipality(municipalityId).then((result) => {
                                if (result?.error) {
                                    toast.error(result.error.data.error.responseMessage);
                                } else {
                                    municipalityList(filters).then((result) => {
                                        const municipalities = result?.data?.records;
                                        setMunicipalities(municipalities);
                                        setTotalRecords(result?.data?.totalRecords);
                                    });
                                    setDisableDelete(true);
                                    toast.info(result?.data?.info?.responseMessage);
                                    onSelectionChange(municipalityId);
                                }
                            });
                        }
                    }}
                />
            ),
            {
                autoClose: false,
                progress: false,
                closeButton: false,
            },
        );
    };

    const openFilter = () => {
        setShowFilter(true);
    };

    const closeFilter = () => {
        setShowFilter(false);
    };

    const onFilter = () => {
        if (formRef.current) {
            formRef.current.handleSubmit();
        }
    };

    const onFiltered = (values) => {
        setFilters({
            ...filters,
            startPage: 0,
            name: values.name,
            postalCode: values.postalCode,
        });
    };

    const onResetFilter = () => {
        setFilters(defaultFilters);
        setShowFilter(false);
        if (formRef.current) {
            formRef.current.resetForm();
        }
    };

    const onPageChange = (currentPage, startPage, limitPage) => {
        setCurrentPage(currentPage);
        setFilters({
            ...filters,
            startPage: startPage,
            limitPage: limitPage,
        });
    };

    useEffect(() => {
        if (selectedMunicipality.length > 0) {
            setDisableDelete(false);
        } else {
            setDisableDelete(true);
        }
    }, [selectedMunicipality]);

    useEffect(() => {
        municipalityList(filters).then((result) => {
            const municipalities = result?.data?.records;
            setMunicipalities(municipalities);
            setTotalRecords(result?.data?.totalRecords);
        });
        setNumberOfUserdFilters(getNumberOfUsedFilters(filters));
    }, [filters, municipalityList]);

    return (
        <DefaultLayout>
            
            <TopNavigation>
                <ButtonBack to="/settings" />
                <Button title={t("municipalities.addNew")} icon="fa-regular fa-plus" onClick={gotoNew}></Button>
                <Button title={t("delete")} icon="fa-regular fa-trash-can" isDisabled={disableDelete} onClick={onDelete}></Button>
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Title>{`${t("municipalities.title")} (${totalRecords})`}</Title>
                <Breadcrumps>
                    <Link title={t("settings.title")} icon="fa-gear" />
                    <Link title={t("settings.default")} />
                    <Link title={t("municipalities.title")} />
                    <Link title={t("settings.list")} isActive={true} />
                </Breadcrumps>
                {!isLoading && (
                    <>
                        <List>
                            <ListHeader>
                                <ListMenu>
                                    <ListMenuButton title="Filter" icon="fa-filter" onClick={openFilter} badge={numberOfUserdFilters} />
                                </ListMenu>
                                <ListFilter isVisible={showFilter} onClose={closeFilter} onFilter={onFilter} onResetFilter={onResetFilter}>
                                    <Formik innerRef={formRef} initialValues={{ name: filters?.name, postalCode: filters?.postalCode }} onSubmit={onFiltered}>
                                        {({ values, handleChange, handleSubmit, resetForm }) => (
                                            <Form>
                                                <Row>
                                                    <Field>
                                                        <Input name="name" label={t("municipalities.name")} value={values.name} onChange={handleChange} />
                                                    </Field>
                                                </Row>
                                                <Row>
                                                    <Field>
                                                        <Input
                                                            name="postalCode"
                                                            label={t("municipalities.postalCode")}
                                                            value={values.postalCode}
                                                            onChange={handleChange}
                                                        />
                                                    </Field>
                                                </Row>
                                            </Form>
                                        )}
                                    </Formik>
                                </ListFilter>
                            </ListHeader>
                            <ListBody>
                                <TableHead>
                                    <TableRow>
                                        <TableHeadCell></TableHeadCell>
                                        <TableHeadCell></TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("municipalities.name")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("municipalities.postalCode")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell></TableHeadCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {municipalities?.length > 0 &&
                                        municipalities?.map((municipality, index) => {
                                            return (
                                                <TableRow key={municipality.id}>
                                                    <TableDataCell>
                                                        <Button icon="fa-regular fa-pen" onClick={() => gotoEdit(municipality.id)}></Button>
                                                    </TableDataCell>
                                                    <TableDataCell>
                                                        <Checkbox
                                                            name="isBlockClass"
                                                            value={municipality.id}
                                                            onChange={() => onSelectionChange(municipality.id)}
                                                        />
                                                    </TableDataCell>
                                                    <TableDataCell>{municipality.name}</TableDataCell>
                                                    <TableDataCell>{municipality.postalCode}</TableDataCell>
                                                    <TableDataCell></TableDataCell>
                                                </TableRow>
                                            );
                                        })}
                                </TableBody>
                            </ListBody>
                        </List>
                        <Pagination currentPage={currentPage} recordsPerPage={recordsPerPage} totalRecords={totalRecords} onClick={onPageChange} />
                    </>
                )}
            </Main>
        </DefaultLayout>
    );
};

export default Municipalities;
